// /orders — 订单列表
// v1.9 P0: 移动端表格退化为 card list
// v1.27.2 fix: 加 <Outlet /> 渲染 /orders/$orderId 子路由
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, createFileRoute, useLocation } from '@tanstack/react-router';
import { useState } from 'react';
import { EmptyState } from '../components/EmptyState';
import { CardListSkeleton, TableSkeleton } from '../components/Skeleton';
import { type CreateOrderInput, type Customer, customerApi, orderApi } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';

export const Route = createFileRoute('/orders')({
  component: OrdersPage,
});

interface OrderRow {
  id: string;
  companyId: string;
  customerId: string;
  poNumber?: string;
  status: string;
  incoterm: string;
  incotermPlace: string;
  totalAmountCents: number;
  currency: string;
  shipDate?: number;
  createdAt: number;
  checklistProgress: {
    completedSteps: number;
    totalSteps: number;
  };
}

const STATUS_LABELS: Record<string, string> = {
  draft: '草稿',
  deposit_received: '已收定金',
  producing: '生产中',
  qc: '验货中',
  balance_received: '已收尾款',
  shipped: '已装船',
  closed: '已关闭',
  cancelled: '已取消',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'tag',
  deposit_received: 'tag tag-stage-quoted',
  producing: 'tag tag-stage-sampled',
  qc: 'tag tag-stage-sampled',
  balance_received: 'tag tag-good',
  shipped: 'tag tag-good',
  closed: 'tag tag-good',
  cancelled: 'tag',
};

function fmt(cents: number, cur: string) {
  const amount = cents / 100;
  if (cur === 'JPY') return `¥${amount.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  return `${cur === 'USD' ? '$' : cur === 'EUR' ? '€' : cur} ${amount.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

function OrdersPage() {
  const { currentCompanyId } = useAuthStore();
  const queryClient = useQueryClient();
  // v1.27.2 fix: 详情时只渲染 Outlet, 不渲染 list
  const location = useLocation();
  const onChild = location.pathname.startsWith('/orders/') && location.pathname !== '/orders';
  const { data: customers } = useQuery({
    queryKey: ['customers', currentCompanyId],
    queryFn: () => customerApi.list({ limit: 200 }),
    enabled: !onChild,
  });
  const { data: ordersData, isLoading } = useQuery({
    queryKey: ['orders', currentCompanyId],
    queryFn: () => orderApi.list(),
    enabled: !onChild,
  });
  const [showCreate, setShowCreate] = useState(false);

  const createMutation = useMutation({
    mutationFn: (input: CreateOrderInput) => orderApi.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      setShowCreate(false);
    },
  });

  if (onChild) return <Outlet />;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 sm:mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold mb-1">订单</h1>
          <p className="text-ink-3 text-xs sm:text-sm">
            共 {ordersData?.total ?? 0} 单 · 流程引导式
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="w-full sm:w-auto px-4 py-2 bg-accent text-white rounded text-sm font-medium hover:bg-accent-2"
        >
          + 新建订单
        </button>
      </div>

      {showCreate && (
        <CreateOrderForm
          customers={customers?.items ?? []}
          onSubmit={(data) => createMutation.mutate(data)}
          onCancel={() => setShowCreate(false)}
          isPending={createMutation.isPending}
          error={(createMutation.error as Error)?.message}
        />
      )}

      <div className="bg-surface border border-line rounded overflow-hidden">
        {isLoading ? (
          <>
            <div className="hidden md:block">
              <TableSkeleton rows={5} />
            </div>
            <div className="md:hidden p-2">
              <CardListSkeleton rows={5} />
            </div>
          </>
        ) : ordersData?.items.length === 0 ? (
          <EmptyState
            icon="list"
            title="还没有订单"
            description="客户接受 PI 后, 在报价详情里点「建订单」, 走 12 步清单流程"
          />
        ) : (
          <>
            {/* 桌面: 表格 */}
            <table className="hidden md:table w-full">
              <thead>
                <tr className="bg-surface-2 text-ink-2 text-xs">
                  <th className="text-left px-4 py-2 font-medium">订单号</th>
                  <th className="text-left px-4 py-2 font-medium">客户</th>
                  <th className="text-left px-4 py-2 font-medium">贸易形式</th>
                  <th className="text-left px-4 py-2 font-medium">金额</th>
                  <th className="text-left px-4 py-2 font-medium">进度</th>
                  <th className="text-left px-4 py-2 font-medium">状态</th>
                  <th className="text-left px-4 py-2 font-medium">装船日</th>
                </tr>
              </thead>
              <tbody>
                {ordersData?.items.map((o: OrderRow) => {
                  const customer = customers?.items.find((c: Customer) => c.id === o.customerId);
                  const pct =
                    (o.checklistProgress.completedSteps / o.checklistProgress.totalSteps) * 100;
                  return (
                    <tr key={o.id} className="border-t border-line hover:bg-surface-2">
                      <td className="px-4 py-3">
                        <Link
                          to="/orders/$orderId"
                          params={{ orderId: o.id }}
                          className="font-medium text-sm text-accent hover:underline"
                        >
                          {o.poNumber || o.id.slice(0, 8)}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-sm">{customer?.name ?? '—'}</td>
                      <td className="px-4 py-3 text-sm">
                        {o.incoterm} {o.incotermPlace.split(' ').slice(1).join(' ')}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium">
                        {fmt(o.totalAmountCents, o.currency)}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-surface-2 rounded">
                            <div
                              className="h-full bg-accent rounded"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="text-ink-3">
                            {o.checklistProgress.completedSteps}/{o.checklistProgress.totalSteps}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={STATUS_COLORS[o.status] || 'tag'}>
                          {STATUS_LABELS[o.status] || o.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-ink-3">
                        {o.shipDate ? new Date(o.shipDate).toLocaleDateString('zh-CN') : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* 移动: 卡片列表 */}
            <ul className="md:hidden p-2 space-y-2">
              {ordersData?.items.map((o: OrderRow) => {
                const customer = customers?.items.find((c: Customer) => c.id === o.customerId);
                const pct =
                  (o.checklistProgress.completedSteps / o.checklistProgress.totalSteps) * 100;
                return (
                  <li
                    key={o.id}
                    className="bg-surface-2 border border-line rounded-lg p-3 animate-fade-up"
                  >
                    <Link to="/orders/$orderId" params={{ orderId: o.id }} className="block">
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm text-accent">
                            {o.poNumber || o.id.slice(0, 8)}
                          </div>
                          <div className="text-xs text-ink-2 mt-0.5 truncate">
                            {customer?.name ?? '—'}
                          </div>
                        </div>
                        <span className={`${STATUS_COLORS[o.status] || 'tag'} shrink-0`}>
                          {STATUS_LABELS[o.status] || o.status}
                        </span>
                      </div>
                      <dl className="text-xs space-y-0.5 mt-2">
                        <div className="flex gap-2">
                          <dt className="text-ink-3 w-14 shrink-0">贸易</dt>
                          <dd className="text-ink-2 truncate">
                            {o.incoterm} {o.incotermPlace.split(' ').slice(1).join(' ')}
                          </dd>
                        </div>
                        <div className="flex gap-2 items-center">
                          <dt className="text-ink-3 w-14 shrink-0">金额</dt>
                          <dd className="text-sm font-medium text-ink">
                            {fmt(o.totalAmountCents, o.currency)}
                          </dd>
                        </div>
                        <div className="flex gap-2 items-center">
                          <dt className="text-ink-3 w-14 shrink-0">进度</dt>
                          <dd className="flex-1 flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-line rounded">
                              <div
                                className="h-full bg-accent rounded"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="text-ink-3 text-[10px]">
                              {o.checklistProgress.completedSteps}/{o.checklistProgress.totalSteps}
                            </span>
                          </dd>
                        </div>
                        {o.shipDate && (
                          <div className="flex gap-2">
                            <dt className="text-ink-3 w-14 shrink-0">装船</dt>
                            <dd className="text-ink-3">
                              {new Date(o.shipDate).toLocaleDateString('zh-CN')}
                            </dd>
                          </div>
                        )}
                      </dl>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

const INCOTERMS = ['EXW', 'FOB', 'CFR', 'CIF', 'DAP', 'DDP'] as const;
const CURRENCIES = ['USD', 'JPY', 'EUR', 'CNY', 'HKD'] as const;

function CreateOrderForm({
  customers,
  onSubmit,
  onCancel,
  isPending,
  error,
}: {
  customers: Customer[];
  onSubmit: (data: CreateOrderInput) => void;
  onCancel: () => void;
  isPending: boolean;
  error?: string;
}) {
  const [form, setForm] = useState({
    customerId: '',
    poNumber: '',
    type: 'bulk' as 'sample' | 'bulk',
    incoterm: 'CIF' as (typeof INCOTERMS)[number],
    incotermPlace: 'CIF Yokohama',
    totalAmountCents: 23000_00,
    currency: 'JPY' as (typeof CURRENCIES)[number],
    depositPct: 30,
    shipDate: '',
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit({
      customerId: form.customerId,
      poNumber: form.poNumber || undefined,
      type: form.type,
      incoterm: form.incoterm,
      incotermPlace: form.incotermPlace,
      totalAmountCents: form.totalAmountCents,
      currency: form.currency,
      depositPct: form.depositPct,
      shipDate: form.shipDate ? new Date(form.shipDate).getTime() : undefined,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="bg-surface border border-line rounded p-4 sm:p-5 mb-4">
      <h2 className="text-sm font-semibold mb-3">新建订单</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-ink-2">
        <label className="flex flex-col gap-1">
          客户 *
          <select
            value={form.customerId}
            onChange={(e) => setForm({ ...form, customerId: e.target.value })}
            className="px-2 py-1.5 border border-line rounded text-sm text-ink"
            required
          >
            <option value="">选择客户</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.country})
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          客户 PO 号
          <input
            type="text"
            value={form.poNumber}
            onChange={(e) => setForm({ ...form, poNumber: e.target.value })}
            className="px-2 py-1.5 border border-line rounded text-sm text-ink"
            placeholder="PO-2026-088"
          />
        </label>
        <label className="flex flex-col gap-1">
          贸易形式
          <select
            value={form.incoterm}
            onChange={(e) =>
              setForm({ ...form, incoterm: e.target.value as (typeof INCOTERMS)[number] })
            }
            className="px-2 py-1.5 border border-line rounded text-sm text-ink"
          >
            {INCOTERMS.map((i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          目的港
          <input
            type="text"
            value={form.incotermPlace.replace(/^[A-Z]+\s/, '')}
            onChange={(e) =>
              setForm({ ...form, incotermPlace: `${form.incoterm} ${e.target.value}` })
            }
            className="px-2 py-1.5 border border-line rounded text-sm text-ink"
            placeholder="Yokohama"
          />
        </label>
        <label className="flex flex-col gap-1">
          总额 (cents)
          <input
            type="number"
            value={form.totalAmountCents}
            onChange={(e) => setForm({ ...form, totalAmountCents: +e.target.value })}
            className="px-2 py-1.5 border border-line rounded text-sm text-ink"
          />
        </label>
        <label className="flex flex-col gap-1">
          币种
          <select
            value={form.currency}
            onChange={(e) =>
              setForm({ ...form, currency: e.target.value as (typeof CURRENCIES)[number] })
            }
            className="px-2 py-1.5 border border-line rounded text-sm text-ink"
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          装船日
          <input
            type="date"
            value={form.shipDate}
            onChange={(e) => setForm({ ...form, shipDate: e.target.value })}
            className="px-2 py-1.5 border border-line rounded text-sm text-ink"
          />
        </label>
        <label className="flex flex-col gap-1">
          定金比例
          <input
            type="number"
            value={form.depositPct}
            onChange={(e) => setForm({ ...form, depositPct: +e.target.value })}
            className="px-2 py-1.5 border border-line rounded text-sm text-ink"
            min={0}
            max={100}
          />
        </label>
      </div>
      {error && (
        <div className="mt-3 px-3 py-2 bg-bad/10 border border-bad rounded text-xs text-bad">
          {error}
        </div>
      )}
      <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="w-full sm:w-auto px-4 py-2 border border-line rounded text-sm hover:bg-surface-2"
        >
          取消
        </button>
        <button
          type="submit"
          disabled={!form.customerId || isPending}
          className="w-full sm:w-auto px-4 py-2 bg-accent text-white rounded text-sm font-medium hover:bg-accent-2 disabled:opacity-50"
        >
          {isPending ? '创建中...' : '创建订单'}
        </button>
      </div>
    </form>
  );
}
